import { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { FileText, Plus, X } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@components/button/button";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

import { type ObservacaoFormData, observacaoFormSchema } from "./observacao-form-schema";

type ObservacaoFormModalProps = {
  title: string;
  submitLabel: string;
  initialObservacao: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit?: (data: ObservacaoFormData) => void;
  textareaDisabled?: boolean;
  submitDisabled?: boolean;
  /** Quando false, não renderiza o botão de envio (ex.: modal somente leitura). */
  showSubmit?: boolean;
};

function ObservacaoFormModal({
  title,
  submitLabel,
  initialObservacao,
  isSubmitting,
  onClose,
  onSubmit = () => {},
  textareaDisabled = false,
  submitDisabled = false,
  showSubmit = true,
}: ObservacaoFormModalProps) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ObservacaoFormData>({
    resolver: zodResolver(observacaoFormSchema),
  });

  useEffect(() => {
    setValue("observation", String(initialObservacao ?? ""));
  }, [initialObservacao, setValue]);

  const fieldDisabled = textareaDisabled || isSubmitting;
  const buttonDisabled = submitDisabled || isSubmitting || fieldDisabled;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="max-h-[90vh] w-[840px] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="text-zinc-800" size={24} />
            <h2 className="text-xl font-semibold text-zinc-800">{title}</h2>
          </div>
          <button type="button" onClick={onClose}>
            <X className="size-5 text-zinc-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col">
            <label htmlFor="observacao-form-modal-field" className="mb-2 text-zinc-800">
              Observação
            </label>
            <div className="flex h-32 items-start gap-2 rounded-lg border border-zinc-800 bg-white px-4 py-3">
              <textarea
                id="observacao-form-modal-field"
                disabled={fieldDisabled}
                {...register("observation")}
                placeholder="Digite a observação"
                className="h-full w-full flex-1 resize-none bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
              />
            </div>
            {errors.observation && (
              <span className="mt-1 text-sm text-red-500">{errors.observation.message}</span>
            )}
          </div>

          {showSubmit ? (
            <Button disabled={buttonDisabled} type="submit" className="mt-2 w-full">
              {isSubmitting ? (
                <SpinnerSimple />
              ) : (
                <>
                  <Plus className="size-5" />
                  {submitLabel}
                </>
              )}
            </Button>
          ) : null}
        </form>
      </div>
    </div>
  );
}

export type { ObservacaoFormModalProps };
export { ObservacaoFormModal };
